import * as React from 'react';

import { Button, Col, PhoneField, Row, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding, LucideGrip } from 'lucide-react';

import { useCoreUserUpdateMutation } from './_graphql/useCoreUserUpdateMutation';
import { AccessCodeDisplay } from './AccessCodeDisplay';

import { FormFooter } from '~/modules/settings/_components/_shared/FormFooter';
import { FormSection } from '~/modules/settings/_components/_shared/FormSection';
import { SettingsContent } from '~/modules/settings/_components/_shared/SettingsContent';
import {
  $currentUser,
  $initUserForm,
  $isUserFormDirty,
  $userForm,
  $updateUserForm,
} from '~/modules/settings/_state/userForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const UserContactInfoTab = () => {
  const t = useTranslations();
  const toast = useToast();
  const currentUser = useAtomValue($currentUser);
  const form = useAtomValue($userForm);
  const updateForm = useSetAtom($updateUserForm);
  const initForm = useSetAtom($initUserForm);
  const isFormDirty = useAtomValue($isUserFormDirty);

  const updateMutation = useCoreUserUpdateMutation();
  const [isSaving, setIsSaving] = React.useState(false);
  const [isRefreshingCode, setIsRefreshingCode] = React.useState(false);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: unknown) => {
      updateForm({
        [field]: value as (typeof form)[K],
      });
    };

  const handleDiscard = () => {
    initForm();
  };

  const handleSave = async () => {
    if (!currentUser) return;

    setIsSaving(true);
    try {
      await updateMutation.mutateAsync({
        id: currentUser.id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phone || null,
      });
      toast.success(
        t.teamAndPermissions.userDetails.contactInfo.messages.updateSuccess,
      );
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(
        t.teamAndPermissions.userDetails.contactInfo.messages.updateError,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefreshCode = async () => {
    setIsRefreshingCode(true);
    try {
      // TODO: Implement actual code regeneration
      await new Promise((resolve) => setTimeout(resolve, 500));
      // Generate a random 6-digit code
      const newCode = Math.floor(100000 + Math.random() * 900000).toString();
      updateForm({ accessCode: newCode });
      toast.success(
        t.teamAndPermissions.userDetails.contactInfo.messages.codeRegenerated,
      );
    } catch (error) {
      console.error('Failed to regenerate code:', error);
      toast.error(
        t.teamAndPermissions.userDetails.contactInfo.messages
          .codeRegenerateError,
      );
    } finally {
      setIsRefreshingCode(false);
    }
  };

  const handleSendViaEmail = () => {
    // TODO: Implement send via email
    toast.info(
      t.teamAndPermissions.userDetails.contactInfo.messages.codeSentViaEmail,
    );
  };

  const handleSendViaText = () => {
    // TODO: Implement send via text
    toast.info(
      t.teamAndPermissions.userDetails.contactInfo.messages.codeSentViaText,
    );
  };

  if (!currentUser) {
    return (
      <SettingsContent size="medium" className="p-5">
        <div className="text-sm text-neutral/50">
          {t.teamAndPermissions.userDetails.loading}
        </div>
      </SettingsContent>
    );
  }

  return (
    <Col className="@UserContactInfoTab h-full">
      <Col gap6 className="h-0 grow overflow-y-auto">
        <SettingsContent size="medium" className="gap-6 p-5">
          {/* Contact Info Section */}
          <FormSection
            icon={LucideBuilding}
            title={t.teamAndPermissions.userDetails.contactInfo.sectionTitle}
          >
            <Col gap4>
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label={
                    t.teamAndPermissions.userDetails.contactInfo.fields
                      .firstName
                  }
                  value={form.firstName}
                  onChange={handleFieldChange('firstName')}
                  isRequired
                />
                <TextField
                  label={
                    t.teamAndPermissions.userDetails.contactInfo.fields.lastName
                  }
                  value={form.lastName}
                  onChange={handleFieldChange('lastName')}
                  isRequired
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label={
                    t.teamAndPermissions.userDetails.contactInfo.fields.email
                  }
                  value={form.email}
                  onChange={handleFieldChange('email')}
                  type="email"
                  isRequired
                />
                <PhoneField
                  label={
                    t.teamAndPermissions.userDetails.contactInfo.fields.phone
                  }
                  value={form.phone}
                  onChange={handleFieldChange('phone')}
                  isRequired
                />
              </div>
            </Col>
          </FormSection>

          {/* Horizontal divider */}
          <div className="border-t border-neutral/10" />

          {/* Access Code Section */}
          <FormSection
            icon={LucideGrip}
            title={
              t.teamAndPermissions.userDetails.contactInfo.accessCode
                .sectionTitle
            }
          >
            <Col gap4>
              <div className="text-sm text-neutral/75">
                {
                  t.teamAndPermissions.userDetails.contactInfo.accessCode
                    .subtitle
                }
              </div>

              <div className="rounded-md bg-neutral/7 px-4 py-3">
                <span className="text-base">
                  {
                    t.teamAndPermissions.userDetails.contactInfo.accessCode
                      .codeSentToPrefix
                  }{' '}
                  <span className="font-semibold">
                    {form.email || 'email@example.com'}
                  </span>{' '}
                  {
                    t.teamAndPermissions.userDetails.contactInfo.accessCode
                      .codeSentToOr
                  }{' '}
                  <span className="font-semibold">
                    {form.phone || '+1 (808) 789-0123'}
                  </span>
                  .
                </span>
              </div>

              {/* Code display and send buttons */}
              <Col gap4>
                <Col gap1>
                  <div className="flex items-center gap-1 text-sm font-semibold">
                    {
                      t.teamAndPermissions.userDetails.contactInfo.accessCode
                        .codeLabel
                    }
                    <span className="text-failure">*</span>
                  </div>
                  <AccessCodeDisplay
                    code={form.accessCode}
                    onRefresh={handleRefreshCode}
                    isRefreshing={isRefreshingCode}
                  />
                </Col>

                <Row gap2>
                  <Button
                    variant="outline"
                    size="small"
                    onPress={handleSendViaEmail}
                  >
                    {
                      t.teamAndPermissions.userDetails.contactInfo.accessCode
                        .sendViaEmail
                    }
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onPress={handleSendViaText}
                  >
                    {
                      t.teamAndPermissions.userDetails.contactInfo.accessCode
                        .sendViaText
                    }
                  </Button>
                </Row>
              </Col>
            </Col>
          </FormSection>
        </SettingsContent>
      </Col>

      <SettingsContent size="medium" className="border-t border-neutral/15">
        <FormFooter
          onDiscard={handleDiscard}
          onSave={handleSave}
          isDiscardDisabled={isSaving}
          isSaveDisabled={isSaving || !isFormDirty}
          className="border-t-0 p-5"
        />
      </SettingsContent>
    </Col>
  );
};
